import React, {FC} from "react";
import {
    Box,
    IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {IMotorcycle} from "../../../../interfaces/Interfaces";
import {MainImage} from "./MainImage";
import {ThumbnailList} from "./ThumbnailList";

interface FullscreenOverlayProps {
    motorcycle: IMotorcycle;
    images: string[];
    mainIndex: number;
    setMainIndex: (index: number) => void;
    errorIndices: number[];
    handleError: (index: number) => void;
    closeFullscreen: () => void;
    handleFullscreenLeft: () => void;
    handleFullscreenRight: () => void;
    thumbnailContainerRef: React.RefObject<HTMLDivElement>;
}

export const FullscreenOverlay: FC<FullscreenOverlayProps> = ({
    motorcycle,
    images,
    mainIndex,
    setMainIndex,
    errorIndices,
    handleError,
    closeFullscreen,
    handleFullscreenLeft,
    handleFullscreenRight,
    thumbnailContainerRef,
}) => {
    const mainImageSrc = `https://fyd.ge/fyd-images/${motorcycle.makerName}/${images[mainIndex]}`;
    return (
        <Box
            onClick={closeFullscreen}
            sx={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                background: (theme) => theme.palette.grey[400],
                zIndex: 1300,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
            }}
        >
            <IconButton
                onClick={(e) => {
                    e.stopPropagation();
                    closeFullscreen();
                }}
                sx={{
                    position: "fixed",
                    top: 16,
                    right: 16,
                    zIndex: 1400,
                    color: (theme) => theme.palette.button.text,
                    background: (theme) => theme.palette.button.primary,
                    "&:hover": {backgroundColor: (theme) => theme.palette.button.hover},
                }}
            >
                <CloseIcon fontSize="large"/>
            </IconButton>
            <IconButton
                onClick={(e) => {
                    e.stopPropagation();
                    handleFullscreenLeft();
                }}
                sx={{
                    position: "fixed",
                    left: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 1400,
                    color: (theme) => theme.palette.button.text,
                    background: (theme) => theme.palette.button.primary,
                    "&:hover": {backgroundColor: (theme) => theme.palette.button.hover},
                }}
            >
                <ArrowBackIosIcon fontSize="large"/>
            </IconButton>
            <IconButton
                onClick={(e) => {
                    e.stopPropagation();
                    handleFullscreenRight();
                }}
                sx={{
                    position: "fixed",
                    right: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 1400,
                    color: (theme) => theme.palette.button.text,
                    background: (theme) => theme.palette.button.primary,
                    "&:hover": {backgroundColor: (theme) => theme.palette.button.hover},
                }}
            >
                <ArrowForwardIosIcon fontSize="large"/>
            </IconButton>
            <Box
                onClick={(e) => e.stopPropagation()}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                    height: "100%",
                    justifyContent: "center",
                    gap: 2,
                    p: 2,
                }}
            >
                <Box
                    sx={{
                        maxWidth: "90vw",
                        maxHeight: "70vh",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <MainImage
                        src={mainImageSrc}
                        alt={`${motorcycle.makerName} ${motorcycle.modelName} fullscreen`}
                        onError={() => handleError(mainIndex)}
                        error={errorIndices.includes(mainIndex)}
                        sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            borderRadius: 2,
                        }}
                    />
                </Box>
                <ThumbnailList
                    images={images}
                    motorcycleName={motorcycle.makerName}
                    mainIndex={mainIndex}
                    setMainIndex={setMainIndex}
                    errorIndices={errorIndices}
                    handleError={handleError}
                    containerRef={thumbnailContainerRef}
                    sx={{
                        position: "fixed",
                        gap: 1,
                        overflowX: "auto",
                        overflowY: "hidden",
                        height: 60,
                        bottom: 16,
                        left: "50%",
                        transform: "translateX(-50%)",
                        justifyContent: "center",
                    }}
                />
            </Box>
        </Box>
    );
};
